import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CategoryDto from '../../../dtos/Categories/CategoryDto';
import CategoryApiService from '../../../api/CategoryApiService';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, Divider, FormLabel, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DialogActions from '@mui/material/DialogActions';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import SaveButton from '../../Common/SaveButton';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';
import { useSnackbar } from '../../Common/SnackbarProvider';

const SOURCE_LOCALE = 'en';
const TRANSLATABLE_LOCALES = [
    { code: 'ru', label: 'Русский' },
    { code: 'fr', label: 'Français' },
];

export default function ModifyCategoryModal (props) {
    const { onClose, open, selectedCategory, categoryApiService, onCancel } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [categoryName, setCategoryName] = useState('');
    const [sortSequence, setSortSequence] = useState(0);
    const [recurring, setRecurring] = useState(false);
    const [translations, setTranslations] = useState({});
    const [manualLocales, setManualLocales] = useState([]);

    const [nameError, setNameError] = useState('');
    const [sortSequenceError, setSortSequenceError] = useState('');

    const isEdit = Boolean(selectedCategory?.ctId);

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        const categoryDto = new CategoryDto();
        categoryDto.ctId = selectedCategory?.ctId ?? 0;
        categoryDto.ctName = categoryName;
        categoryDto.ctSortSeq = sortSequence;
        categoryDto.recurring = recurring;
        categoryDto.names = { [SOURCE_LOCALE]: categoryName, ...translations };
        categoryDto.manualLocales = manualLocales;

        if(validateInfo() && selectedCategory && selectedCategory.ctId) {
            const updatedCategory = await categoryApiService.updateCategory(categoryDto);

            if(updatedCategory.success) {
                snackbar.success(t('modifyCategory.toast.updated'));
                resetModal();
            } else {
                snackbar.error(t('modifyCategory.toast.updateError', { error: updatedCategory.message }));
            }
        } else if(validateInfo()) {
            const newCategory = await categoryApiService.createCategory(categoryDto);

            if(newCategory.success) {
                snackbar.success(t('modifyCategory.toast.added'));
                resetModal();
            } else {
                snackbar.error(t('modifyCategory.toast.addError', { error: newCategory.message }));
            }
        }
    });

    function validateInfo() {
        const errors = [];

        if(categoryName === null || categoryName === '') {
            errors.push(t('modifyCategory.validation.nameRequired'));
        }

        setNameError(!categoryName);

        if(sortSequence < 1) {
            errors.push(t('common.validation.sortSequence'));
        }

        setSortSequenceError(sortSequence < 1);

        if(errors.length > 0) {
            snackbar.error(t('modifyCategory.validation.failed', { errors: errors.join(', ') }));
            return false;
        } else {
            return true;
        }
    }

    function resetModal() {
        setSortSequence(0);
        setCategoryName('');
        setTranslations({});
        setManualLocales([]);
        onClose();
    }

    function onTranslationChange(locale, value) {
        setTranslations(prev => ({ ...prev, [locale]: value }));
        if (!manualLocales.includes(locale)) {
            setManualLocales(prev => [...prev, locale]);
        }
    }

    function onClearOverride(locale) {
        setTranslations(prev => {
            const next = { ...prev };
            delete next[locale];
            return next;
        });
        setManualLocales(prev => prev.filter(l => l !== locale));
    }

    const helperText = useMemo(() => isEdit
        ? t('modifyCategory.translations.helperEdit')
        : t('modifyCategory.translations.helperCreate'), [isEdit, t]);

    useEffect(() => {
        if(open) {
            setCategoryName(selectedCategory?.ctName ?? '');
            setSortSequence(selectedCategory?.ctSortSeq ?? 0);
            setRecurring(selectedCategory?.recurring ?? false);
            const incomingNames = selectedCategory?.names ?? {};
            const nonSource = Object.fromEntries(
                Object.entries(incomingNames).filter(([locale]) => locale !== SOURCE_LOCALE)
            );
            setTranslations(nonSource);
            setManualLocales(selectedCategory?.manualLocales ?? []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, open])

    return(
        <Dialog
            open={open}
            sx={{ '& .MuiDialog-paper': { width: '100%' } }}
            maxWidth={'lg'}
            fullWidth
        >
            <DialogTitle>{selectedCategory?.ctName ? t('modifyCategory.title.edit', { name: selectedCategory.ctName }) : t('modifyCategory.title.create')}</DialogTitle>
            <DialogContent dividers>
                <Box>
                    <Box sx={{ mb: 2 }}>
                        <FormLabel>{t('modifyCategory.field.name')}</FormLabel>
                        <br/>
                        <TextField
                            error={nameError}
                            sx={{ width: '100%' }}
                            autoComplete={'Category Name'}
                            value={categoryName}
                            onChange={(event) => setCategoryName(event.target.value)}
                        />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <FormLabel>{t('fieldName.sortSequence')}</FormLabel>
                        <br/>
                        <TextField
                            error={sortSequenceError}
                            sx={{ width: '100%' }}
                            type={'number'}
                            autoComplete={'Sort Sequence'}
                            value={sortSequence}
                            onChange={(event) => setSortSequence(event.target.value)}
                        />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={<Checkbox checked={recurring}
                                               onChange={() => setRecurring(!recurring)}
                        />}
                            label={t('modifyCategory.field.recurring')}
                        />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                        {t('modifyCategory.translations.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        {helperText}
                    </Typography>
                    <Stack spacing={1.5}>
                        {TRANSLATABLE_LOCALES.map(({ code, label }) => {
                            const isManual = manualLocales.includes(code);
                            return (
                                <Box key={code} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FormLabel sx={{ minWidth: 90 }}>{label}</FormLabel>
                                    <TextField
                                        size="small"
                                        sx={{ flex: 1 }}
                                        value={translations[code] ?? ''}
                                        placeholder={isEdit ? '' : t('modifyCategory.translations.willGenerate')}
                                        onChange={(e) => onTranslationChange(code, e.target.value)}
                                        InputProps={{
                                            endAdornment: isManual ? (
                                                <Tooltip title={t('modifyCategory.translations.regenerateTooltip')}>
                                                    <IconButton size="small" onClick={() => onClearOverride(code)}>
                                                        <RefreshRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null,
                                        }}
                                        helperText={isManual ? t('modifyCategory.translations.manualBadge') : t('modifyCategory.translations.autoBadge')}
                                    />
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button autoFocus variant={'contained'} onClick={onCancel} disabled={saving}>{t('common.cancel')}</Button>
                <SaveButton loading={saving} variant={'contained'} onClick={handleSave}>{t('common.save')}</SaveButton>
            </DialogActions>
        </Dialog>
    );
}

ModifyCategoryModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    selectedCategory: PropTypes.objectOf(CategoryDto),
    categoryApiService: PropTypes.objectOf(CategoryApiService).isRequired,
    onCancel: PropTypes.func.isRequired,
}
